import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { useTheme } from '../../contexts/ThemeContext';
import useLookupOptions from '../../hooks/useLookupOptions';

const SelectField = ({
  id,
  name,
  value,
  onChange,
  options = [],
  label,
  disabled = false,
  className = '',
  placeholder = '-- Seleziona --',
  required = false,
  error,
  labelClassName = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
  isLoading = false,
  isClearable = true,
  returnFullOption = false,
  themeColor,
  lookupConfig,
  apiEndpoint,
  showForeignKeyInfo = false,
}) => {
  const { theme } = useTheme();
  
  const isDark = theme === 'dark' || document.documentElement.classList.contains('dark');
  const currentThemeColor = themeColor || 'primary';
  
  const [isMounted, setIsMounted] = useState(false);
  const [inputValue, setInputValue] = useState(''); // ✅ CORREZIONE: Inizializza sempre vuoto
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const { lookupOptions, loading: lookupLoading, error: lookupError } = useLookupOptions(lookupConfig, apiEndpoint);

  // ✅ CORREZIONE: Filtra le opzioni escluse se specificato
  const filteredOptions = useMemo(() => {
    if (!lookupConfig?.excludeValues || lookupConfig.excludeValues.length === 0) {
      return options;
    }
    
    return options.filter(option => 
      !lookupConfig.excludeValues.includes(option.value)
    );
  }, [options, lookupConfig?.excludeValues]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ✅ CORREZIONE: Stabilizza selectedOption con useMemo
  const selectedOption = useMemo(() => {
    if (!value && value !== 0) {
      return null;
    }
    
    const found = filteredOptions.find(option => String(option.value) === String(value));
    // Se il valore non è tra le opzioni ma esiste, mostra comunque come opzione sintetica
    // Questo gestisce valori dinamici (es. .max(50)) non presenti nelle opzioni predefinite
    if (!found && value !== '' && value !== null && value !== undefined) {
      return { value, label: String(value) };
    }
    return found || null;
  }, [filteredOptions, value]);

  // ✅ CORREZIONE: Gestione cambiamenti migliorata
  const handleChange = useCallback((selected) => {

    
    if (returnFullOption) {
      onChange(selected);
    } else {
      // Se non c'è selezione, invia 0 invece di null per i campi numerici
      onChange(selected ? selected.value : 0);
    }
  }, [onChange, returnFullOption, name]);

  // ✅ CORREZIONE PRINCIPALE: Gestione input che non interferisce con il valore selezionato
  const handleInputChange = useCallback((newInputValue, { action }) => {

    
    if (action === 'input-change') {
      // Solo quando l'utente sta digitando, aggiorna inputValue
      setInputValue(newInputValue);
    } else if (action === 'menu-close') {
      // ✅ CORREZIONE: Quando il menu si chiude, resetta inputValue a vuoto
      // Questo evita che il valore selezionato venga usato come filtro
      setInputValue('');
    } else if (action === 'set-value') {
      // ✅ CORREZIONE: Quando viene selezionato un valore, resetta inputValue
      setInputValue('');
    }
  }, [name]);

  // ✅ CORREZIONE: Gestione apertura/chiusura menu
  const handleMenuOpen = useCallback(() => {

    setMenuIsOpen(true);
    // ✅ CORREZIONE: Quando il menu si apre, assicurati che inputValue sia vuoto
    // in modo da mostrare tutte le opzioni
    setInputValue('');
  }, [name]);

  const handleMenuClose = useCallback(() => {

    setMenuIsOpen(false);
    // ✅ CORREZIONE: Quando il menu si chiude, resetta inputValue
    setInputValue('');
  }, [name]);

  // ✅ RIMOSSO: Non aggiornare più inputValue quando selectedOption cambia
  // Questo era la causa del problema - il valore selezionato veniva usato come filtro
  
  // ✅ AGGIUNTO: Funzione per creare il portal target
  const getMenuPortalTarget = useCallback(() => {
    let portalContainer = document.getElementById('react-select-menu-portal');
    if (!portalContainer) {
      portalContainer = document.createElement('div');
      portalContainer.id = 'react-select-menu-portal';
      portalContainer.style.cssText = 'position: relative; z-index: 999999 !important;';
      document.body.appendChild(portalContainer);
    }
    return portalContainer;
  }, []);

  // Generate a theme color function
  const getThemeColor = (colorName) => {
    const colorMap = {
      primary: '#3b82f6',
      emerald: '#10b981',
      blue: '#3b82f6',
      purple: '#8b5cf6',
      green: '#10b981',
      red: '#ef4444',
      yellow: '#f59e0b',
      indigo: '#6366f1',
      pink: '#ec4899',
      gray: '#6b7280',
    };
    return colorMap[colorName] || colorMap.primary;
  };

  const themeColorValue = getThemeColor(currentThemeColor);

  // ✅ CORREZIONE: Opzioni formattate stabilizzate
  const formattedOptions = useMemo(() => {
    return filteredOptions.map(option => {
      let label = option.label || '';
      
      if (showForeignKeyInfo) {
        const foreignKeyTexts = [];
        
        Object.keys(option).forEach(key => {
          if (key.endsWith('_text') && option[key] && option[key].trim() !== '') {
            foreignKeyTexts.push(option[key]);
          }
        });
        
        if (foreignKeyTexts.length > 0) {
          label = `${label} (${foreignKeyTexts.join(', ')})`;
        }
      }
      
      return {
        value: option.value,
        label: label,
        original: option,
      };
    });
  }, [filteredOptions, showForeignKeyInfo]);

  // ✅ CORREZIONE: Chiave stabile per il componente
  const selectKey = useMemo(() => {
    return `${id}-${formattedOptions.length}-${value}-${JSON.stringify(lookupConfig?.excludeValues || [])}`;
  }, [id, formattedOptions.length, value, lookupConfig?.excludeValues]);

  // Calcola l'altezza massima dinamica del menu
  const dynamicMaxMenuHeight = useMemo(() => {
    const optionHeight = 40;
    const menuPadding = 8;
    const heightForOptions = formattedOptions.length * optionHeight + menuPadding;
    const maxHeightCap = 280;
    const heightForFiveOptions = 216;

    if (formattedOptions.length < 5) {
      return Math.max(heightForOptions, optionHeight + menuPadding);
    } else {
      return Math.min(heightForFiveOptions, maxHeightCap);
    }
  }, [formattedOptions.length]);

  // Custom styles per React-Select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      border: error 
        ? '2px solid #ef4444' 
        : state.isFocused 
          ? `2px solid ${themeColorValue}`
          : `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
      borderRadius: '8px',
      boxShadow: state.isFocused 
        ? `0 0 0 3px ${themeColorValue}20`
        : isDark 
          ? '0 2px 4px -1px rgba(0, 0, 0, 0.3)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      backgroundColor: isDark ? '#374151' : '#ffffff',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: state.isFocused 
          ? themeColorValue 
          : isDark ? '#6b7280' : '#9ca3af',
      },
    }),
    
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
      boxShadow: isDark 
        ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
        : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      backgroundColor: isDark ? '#374151' : '#ffffff',
      zIndex: 999999,
    }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 999999,
    }),

    menuList: (provided) => ({
      ...provided,
      padding: '4px',
      maxHeight: '280px',
      overflowY: 'auto',
    }),

    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? themeColorValue
        : state.isFocused
          ? isDark
            ? '#4b5563'
            : '#f3f4f6'
          : isDark 
            ? '#374151'
            : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : isDark
          ? '#f3f4f6'
          : '#111827',
      '&:hover': {
        backgroundColor: state.isSelected
          ? themeColorValue
          : isDark
            ? '#4b5563'
            : '#f3f4f6',
        color: state.isSelected
          ? '#ffffff'
          : isDark
            ? '#ffffff'
            : '#111827',
      },
      fontSize: '14px',
      padding: '10px 12px',
      cursor: 'pointer',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
    }),

    singleValue: (provided) => ({
      ...provided,
      color: isDark ? '#f3f4f6' : '#111827',
      fontSize: '14px',
    }),

    input: (provided) => ({
      ...provided,
      color: isDark ? '#f3f4f6' : '#111827',
      caretColor: themeColorValue,
    }),

    placeholder: (provided) => ({
      ...provided,
      color: isDark ? '#9ca3af' : '#6b7280',
      fontSize: '14px',
    }),

    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused
        ? themeColorValue
        : isDark ? '#9ca3af' : '#6b7280',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        color: themeColorValue,
      },
    }),

    clearIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused
        ? themeColorValue
        : isDark ? '#9ca3af' : '#6b7280',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        color: '#ef4444',
      },
    }),

    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: isDark ? '#4b5563' : '#d1d5db',
    }),

    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 8px',
    }),

    loadingIndicator: (provided) => ({
      ...provided,
      color: themeColorValue,
    }),

    noOptionsMessage: (provided) => ({
      ...provided,
      color: isDark ? '#f3f4f6' : '#6b7280',
      fontSize: '14px',
      padding: '12px',
      backgroundColor: isDark ? '#374151' : '#ffffff',
    }),

    loadingMessage: (provided) => ({
      ...provided,
      color: isDark ? '#f3f4f6' : '#6b7280',
      fontSize: '14px',
      padding: '12px',
      backgroundColor: isDark ? '#374151' : '#ffffff',
    }),
  };

  // Prevent rendering during SSR hydration mismatch
  if (!isMounted) {
    return null;
  }

  // Don't render the Select until we have options (unless explicitly loading)
  if (!isLoading && formattedOptions.length === 0) {
    return (
      <div className={className}>
        {label && (
          <label htmlFor={id} className={labelClassName}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className={`text-sm p-3 border rounded-lg transition-all duration-200
                        ${isDark 
                          ? 'text-gray-400 bg-gray-700 border-gray-600' 
                          : 'text-gray-500 bg-gray-50 border-gray-300'
                        }`}>
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`}></div>
            Nessuna opzione disponibile
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Select
          key={selectKey}
          id={id}
          name={name}
          options={formattedOptions}
          value={selectedOption}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          placeholder={placeholder}
          isDisabled={disabled}
          isLoading={isLoading || lookupLoading}
          isClearable={isClearable}
          styles={customStyles}
          className="react-select-container"
          classNamePrefix="react-select"
          menuPlacement="auto"
          menuPosition="fixed"
          menuPortalTarget={getMenuPortalTarget()}
          isSearchable={true}
          maxMenuHeight={dynamicMaxMenuHeight}
          menuShouldScrollIntoView={false}
          filterOption={(option, inputValue) => {
            // ✅ CORREZIONE: Solo filtra se c'è effettivamente un inputValue
            if (!inputValue || inputValue.trim() === '') {
              return true; // Mostra tutte le opzioni se non c'è ricerca
            }
            
            const searchTerm = inputValue.toLowerCase();
            const labelMatch = option.label.toLowerCase().includes(searchTerm);
            const valueMatch = String(option.value).toLowerCase().includes(searchTerm);
            return labelMatch || valueMatch;
          }}
          noOptionsMessage={() => "Nessuna opzione trovata"}
          loadingMessage={() => "Caricamento..."}
          inputValue={inputValue}
        />
      </div>
      
      {error && (
        <div className="mt-2 flex items-center">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
          <p className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default SelectField;